import { useCallback, useEffect, useState } from "react";
import axios from "axios";

/**
 * Performs the GET /admin/reservation/last request and pushes results through setters.
 * The endpoint returns a plain array of up to 10 enriched reservations.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function fetchLast({ signal, setItems, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const { data } = await axios.get("/admin/reservation/last");
        if (!signal.aborted) setItems(Array.isArray(data) ? data : []);
    } catch (err) {
        if (!signal.aborted) {
            const msg = err?.response?.data;
            setError(typeof msg === "string" ? msg : "Impossible de charger les réservations");
        }
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Hook returning the latest reservations list state.
 * Exposes refresh for manual re-fetch (e.g. after detail drawer actions land later).
 * @returns {{ items: Array, loading: boolean, error: string|null, refresh: Function }}
 */
function useLastReservations() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        fetchLast({ signal: controller.signal, setItems, setError, setLoading });
        return () => controller.abort();
    }, [tick]);

    const refresh = useCallback(() => setTick((t) => t + 1), []);
    return { items, loading, error, refresh };
}

export default useLastReservations;
